import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

/** A Chrome / Android „telepíthető" eseménye (nincs a szabványos TS típusok között). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const INSTALL_DISMISSED_KEY = 'pwa.installDismissedAt';
/** Elutasítás után ennyi ideig nem ajánljuk fel újra a telepítést. */
const INSTALL_SNOOZE_MS = 14 * 24 * 3600 * 1000;

/**
 * PWA-viselkedés egy helyen:
 *  - frissítés: új verziónál jelzünk, a felhasználó egy koppintással frissít (nem kell „kemény frissítés");
 *  - telepítés: Androidon / asztali Chrome-ban a böngésző telepítő-ablakát nyitjuk,
 *    iOS Safariban (ahol nincs ilyen) rövid útmutatót mutatunk.
 */
@Injectable({ providedIn: 'root' })
export class PwaService {
  /** új verzió letöltve, aktiválásra vár */
  readonly updateReady$ = new BehaviorSubject<boolean>(false);
  /** 'prompt' = a böngésző telepítő-ablaka elérhető; 'ios' = kézi útmutató kell; null = nincs teendő */
  readonly installMode$ = new BehaviorSubject<'prompt' | 'ios' | null>(null);
  /** nincs internetkapcsolat (a böngésző jelzése alapján) */
  readonly offline$ = new BehaviorSubject<boolean>(!navigator.onLine);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  constructor(private updates: SwUpdate) {}

  init(): void {
    this.watchUpdates();
    this.watchInstall();
    window.addEventListener('offline', () => this.offline$.next(true));
    window.addEventListener('online', () => this.offline$.next(false));
  }

  /** Telepítve (kezdőképernyőről) fut-e az app. */
  get isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  }

  // ---------------------------------------------------------------- frissítés

  private watchUpdates(): void {
    if (!this.updates.isEnabled) {
      return;
    }
    this.updates.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.updateReady$.next(true));
    // Sérült gyorsítótárnál az egyetlen gyógymód az újratöltés.
    this.updates.unrecoverable.subscribe(() => document.location.reload());

    // Új verzió keresése: indulás után egy perccel, utána 15 percenként, és amikor az app előtérbe kerül.
    // (Az `isStable`-re nem várhatunk: a háttér-időzítők miatt az app sosem „nyugszik meg".)
    timer(60 * 1000, 15 * 60 * 1000).subscribe(() => this.check());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.check();
      }
    });
  }

  private check(): void {
    this.updates.checkForUpdate().catch(() => undefined); // offline — majd legközelebb
  }

  async applyUpdate(): Promise<void> {
    try {
      await this.updates.activateUpdate();
    } finally {
      document.location.reload();
    }
  }

  // ---------------------------------------------------------------- telepítés

  private watchInstall(): void {
    if (this.isStandalone || this.recentlyDismissed()) {
      return;
    }
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault(); // a saját, időzített sávunkból ajánljuk fel
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.installMode$.next('prompt');
    });
    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.installMode$.next(null);
    });
    // iOS / iPadOS Safari: nincs telepítő-esemény — a Megosztás menüből lehet a kezdőképernyőre tenni.
    const ua = navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIos && isSafari) {
      this.installMode$.next('ios');
    }
  }

  async install(): Promise<void> {
    const prompt = this.deferredPrompt;
    if (!prompt) {
      return;
    }
    await prompt.prompt();
    const choice = await prompt.userChoice;
    this.deferredPrompt = null;
    this.installMode$.next(null);
    if (choice.outcome === 'dismissed') {
      this.dismissInstall();
    }
  }

  dismissInstall(): void {
    try {
      localStorage.setItem(INSTALL_DISMISSED_KEY, `${Date.now()}`);
    } catch {
      // privát mód — nem baj
    }
    this.installMode$.next(null);
  }

  private recentlyDismissed(): boolean {
    try {
      const at = Number(localStorage.getItem(INSTALL_DISMISSED_KEY) || 0);
      return !!at && Date.now() - at < INSTALL_SNOOZE_MS;
    } catch {
      return false;
    }
  }
}
